import { alpha, ListItem, Typography, useTheme } from '@mui/material';

const ICON_SIZE = 24;

interface IProps {
  Icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  label: string;
  isExpanded?: boolean;
  onClick: () => void;
}

/**
 * Пункт меню-действие: выглядит как NavItem, но не ведёт на роут, а вызывает
 * колбэк (например, открывает модалку). Активным не бывает — градиент не нужен.
 * Корень — ListItem, тот же что у NavItem: так совпадают отступы и высота ряда,
 * и на пункт действует правило тач-таргета Drawer'а (.MuiListItem-root).
 * @param root0 - пропсы
 * @param root0.Icon - иконка пункта
 * @param root0.label - подпись пункта
 * @param root0.isExpanded - развёрнут ли сайдбар
 * @param root0.onClick - обработчик клика
 */
export const NavActionItem = ({ Icon, label, isExpanded, onClick }: IProps) => {
  const { palette } = useTheme();

  /**
   * Клавиатурная активация: ListItem — не нативная кнопка, Enter/Space надо обработать самим
   * @param event - событие клавиатуры
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    // Space по умолчанию скроллит страницу
    event.preventDefault();
    onClick();
  };

  return (
    <ListItem
      aria-label={label}
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{
        alignItems: 'center',
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        gap: 2,
        margin: '0 8px',
        padding: '8px 12px',
        position: 'relative',
        transition: 'all 0.3s ease-in-out',
        width: 'calc(100% - 16px)',
        '&:hover': {
          backgroundColor: alpha(palette.common.white, 0.12)
        }
      }}
    >
      <Icon
        style={{
          height: ICON_SIZE,
          // В NavItem перед иконкой стоит невидимый svg с градиентом, и gap
          // сдвигает иконку на 8px вправо — компенсируем, чтобы столбец совпадал
          marginLeft: 8,
          minHeight: ICON_SIZE,
          minWidth: ICON_SIZE,
          stroke: palette.common.white,
          width: ICON_SIZE
        }}
      />

      {isExpanded && (
        <Typography
          variant='subtitle1'
          sx={{
            animation: 'fadeIn 0.3s ease-in-out forwards',
            color: palette.common.white,
            left: '64px',
            opacity: 0,
            overflow: 'hidden',
            position: 'absolute',
            right: '20px',
            textAlign: 'left',
            textOverflow: 'ellipsis',
            textWrap: 'nowrap',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 }
            }
          }}
        >
          {label}
        </Typography>
      )}
    </ListItem>
  );
};
